import { useContext, useMemo } from 'react';

import RewardHeading from '../RewardHeading';
import RewardOverrideControl from '../RewardOverrideControl';
import hasActiveRewardOverride from '../../utils/hasActiveRewardOverride';
import formatRewardRate from '../../utils/formatRewardRate';
import { BonusOverviewContext } from '../../providers/BonusOverviewProvider';
import useValueFormatters from '../../../../hooks/useValueFormatters';
import toSentenceCase from '../../../../services/toSentenceCase';
import {
  getMaxRewardBandPoints,
  isArnoldBrosRewardBands,
} from '../../utils/rewardBandsUtils';
import AggregatedRewardTooltip from '../AggregatedRewardTooltip';
import getRewardUnits from '../../utils/getRewardUnits';
import isPointsToPayConversionReward from '../../utils/isPointsToPayConversionReward';
import PointsToPayConversionTooltip from '../PointsToPayConversionTooltip';
import resolveRewardWithCriteria from '../../utils/resolveRewardWithCriteria';

const BonusRewardHeading = ({
  reward,
  criteria,
  eligibleForBonus,
  isLoadingPreview,
  rewardType,
  isOverrideMode,
  isOverridable,
  isOverridden,
  units,
}: {
  reward: BonusTypes.Rewards.AnyRewardOutput;
  criteria: BonusTypes.Rewards.CriteriaOutput[];
  eligibleForBonus: 'qualified' | 'disqualified';
  isLoadingPreview: boolean;
  rewardType: 'none' | 'points' | 'monetary';
  isOverrideMode: boolean;
  isOverridable: boolean;
  isOverridden: boolean;
  units?: string;
}) => {
  const { formatValue } = useValueFormatters();

  const isPassed = useMemo((): boolean => {
    if (criteria.length > 0) {
      return criteria.every((c) => c.passed);
    }
    const { rewardBands } = reward;
    if (rewardBands && isArnoldBrosRewardBands(rewardBands)) {
      return rewardBands.passed;
    }
    return true;
  }, [criteria, reward]);

  const isEligible = useMemo((): boolean => {
    return eligibleForBonus === 'qualified';
  }, [eligibleForBonus]);

  const rewardBand = useMemo(() => {
    const { rewardBands } = reward;
    if (!rewardBands || isArnoldBrosRewardBands(rewardBands)) {
      return undefined;
    }

    return rewardBands.bands.find((b) => b.active);
  }, [reward]);

  const isPointsToPayConversion = useMemo(
    () => isPointsToPayConversionReward({ reward }),
    [reward],
  );

  const label = useMemo((): string => {
    return toSentenceCase(reward.name);
  }, [reward.name]);

  const currentPay = useMemo((): number | undefined => {
    if (isPointsToPayConversion) {
      if (reward.reward !== undefined) {
        return reward.reward;
      }
      if (reward.type === 'fixedBanded') {
        return rewardBand?.value;
      }
      if (reward.type === 'multiplierBanded') {
        return rewardBand?.reward ?? undefined;
      }
    }

    if (reward.reward !== undefined) {
      return reward.reward;
    }

    return reward.potentialReward;
  }, [
    isPointsToPayConversion,
    reward.potentialReward,
    reward.reward,
    reward.type,
    rewardBand,
  ]);

  const formattedCurrentPay = useMemo((): string => {
    if (currentPay === undefined) {
      return '';
    }

    if (rewardType === 'monetary' || isPointsToPayConversion) {
      return formatValue({
        value: currentPay,
        formatting: { prefix: 'currency', precision: 2, currencyType: 'USD' },
      });
    }

    const earned = formatValue({
      value: currentPay,
      formatting: { precision: 0 },
    });
    const maxPoints = getMaxRewardBandPoints(reward.rewardBands);
    if (maxPoints !== undefined) {
      const max = formatValue({
        value: maxPoints,
        formatting: { precision: 0 },
      });
      return `${earned} / ${max} PTS`;
    }
    return `${earned} PTS`;
  }, [
    currentPay,
    formatValue,
    isPointsToPayConversion,
    reward.rewardBands,
    rewardType,
  ]);

  const paySubTitle = useMemo((): string | undefined => {
    if (rewardType === 'points') {
      return undefined;
    }

    const { rewardBands } = reward;

    // Resolve the line's per-unit monetary RATE, but ONLY for reward types where the value is
    // genuinely a $/unit rate — keyed on `type`, not band shape, since several banded types
    // carry a `multiplierField` whose band value is NOT a rate:
    // - `multiplier` (no bands): flat rate on `multiplier.multiplierValue`.
    // - `multiplierBanded`: the active band's value is the $/unit rate (none active ⇒ no rate).
    // - Arnold `scaledMultiplierBanded`: only a 'multiply' combine carries a pay-per-unit rate.
    // Everything else (fixed, fixedBanded, and derived/scaled types like `scaledDerivedBanded`,
    // whose band value is a tenure/scaling FACTOR, not dollars) has no per-unit rate subtitle.
    let rate: number | undefined;
    if (reward.type === 'multiplierBanded') {
      rate = rewardBand?.value;
    } else if (!rewardBands) {
      const { multiplier } = reward;
      rate =
        multiplier && typeof multiplier === 'object'
          ? multiplier.multiplierValue
          : undefined;
    } else if (isArnoldBrosRewardBands(rewardBands)) {
      rate =
        rewardBands.combineMethod === 'multiply'
          ? rewardBands.payPerMile
          : undefined;
    }

    // Suppress a $0 / missing rate: show no subtitle (and let override mode fall back to
    // "Adjust payout") rather than a misleading "($0)".
    if (!rate) {
      return undefined;
    }

    return `(${formatRewardRate({ rate, rewardType, units })})`;
  }, [reward, rewardBand, rewardType, units]);

  // In override mode, an overridable reward line's rate subtitle becomes the payout-override
  // dropdown (labelled with the current rate). Otherwise the static subtitle is shown.
  const subtitleNode =
    isOverrideMode && isOverridable ? (
      <RewardOverrideControl rewardName={reward.name} label={paySubTitle} />
    ) : undefined;

  // A reward-level payout override makes the line pay out regardless of its criteria, so treat
  // it as "passed" for the header tick + pay colour (otherwise an overridden-but-criteria-failing
  // reward like Fuel renders grey/✗ despite paying). The failing criterion still shows in the body.
  const isRewardActive = isPassed || isOverridden;

  const payInfoTooltip = useMemo(() => {
    if (isPointsToPayConversion) {
      return <PointsToPayConversionTooltip reward={reward} />;
    }

    if (reward.type !== 'aggregated') {
      return undefined;
    }

    const hasBandedSubRewards = (reward.rewards ?? []).some(
      (subReward) =>
        !!subReward.rewardBands &&
        !isArnoldBrosRewardBands(subReward.rewardBands),
    );

    if (!hasBandedSubRewards) {
      return undefined;
    }

    return <AggregatedRewardTooltip reward={reward} />;
  }, [isPointsToPayConversion, reward]);

  return (
    <RewardHeading
      rewardType={rewardType}
      isEligible={isEligible}
      isPassed={isRewardActive}
      label={label}
      currentPay={formattedCurrentPay}
      paySubTitle={paySubTitle}
      rewardBands={isPointsToPayConversion ? undefined : reward.rewardBands}
      isLoadingPreview={isLoadingPreview}
      subtitleNode={subtitleNode}
      isOverridden={isOverridden}
      payInfoTooltip={payInfoTooltip}
      payInfoTooltipTestId={`pay-info-tooltip-${reward.name}`}
      rewardBandsTooltipTestId={`reward-bands-tooltip-${reward.name}`}
    />
  );
};

const Gate = ({ rewardName }: { rewardName: string }) => {
  const {
    isLoading,
    bonusOverview,
    isLoadingPreview,
    currentMode,
    rewardOverrideOptions,
  } = useContext(BonusOverviewContext);
  const rewardWithCriteria = useMemo(() => {
    if (!bonusOverview) {
      return undefined;
    }
    return resolveRewardWithCriteria({
      rewards: bonusOverview.rewardOutput.rewards,
      criteria: bonusOverview.rewardOutput.criteria,
      rewardName,
    });
  }, [bonusOverview, rewardName]);

  if (isLoading || !bonusOverview || !rewardWithCriteria) {
    return null;
  }

  // Active reward-level override on this line: a staged Set, or a persisted override not being
  // cleared. Drives the "overridden" marker on the pay figure (shown in both view + override modes).
  const isOverridden = hasActiveRewardOverride({
    rewardName,
    persistedRewardOverrides: bonusOverview.actualRewardOutput.rewardOverrides,
    stagedRewardOverrides: bonusOverview.stagedOverrides?.rewardOverrides,
  });

  return (
    <BonusRewardHeading
      eligibleForBonus={bonusOverview.rewardOutput.eligibleForBonus}
      criteria={rewardWithCriteria.criteria}
      reward={rewardWithCriteria.reward}
      units={getRewardUnits({
        bonusProgram: bonusOverview.bonusProgram,
        rewardName,
      })}
      isLoadingPreview={isLoadingPreview}
      rewardType={bonusOverview.bonusProgram.rewardType}
      isOverrideMode={currentMode.mode === 'override'}
      isOverridable={!!rewardOverrideOptions?.get(rewardName)}
      isOverridden={isOverridden}
    />
  );
};

export default Gate;
